import { Button, Classes, Dialog, Icon } from '@blueprintjs/core'
import { lang } from 'botpress/shared'
import React from 'react'
import '../../../user/modern-settings.css'

export interface BotInfoDialogProps {
  isOpen: boolean
  toggleDialog: () => void
  handleNameChanged: (e: React.ChangeEvent<HTMLInputElement>) => void
  handlePromptChanged: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  handleTemplateChanged: (selectedTemplate: any) => void
  openChannelDialog: (e: React.MouseEvent) => void
  isProcessing: boolean
  botName: string
  botPrompt: string
  selectedTemplate?: any
  templates: any[]
}

const BotInfoDialog: React.FC<BotInfoDialogProps> = ({
  isOpen,
  toggleDialog,
  handleNameChanged,
  handlePromptChanged,
  handleTemplateChanged,
  openChannelDialog,
  isProcessing,
  botName,
  selectedTemplate,
  templates,
}) => {

  const isButtonDisabled = () => {
    if (!botName.trim() || isProcessing) {
      return true
    }
    if (!selectedTemplate) {
      return true
    }
    return false
  }

  return (
    <Dialog
      title={lang.tr('admin.workspace.bots.create.newBot')}
      icon='add'
      isOpen={isOpen}
      onClose={toggleDialog}
      transitionDuration={0}
      canOutsideClickClose={false}
      className='modern-dialog'
      style={{ width: '540px' }}
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <div className={Classes.DIALOG_BODY}>
          <div
            style={{
              fontSize: '13px',
              color: '#64748b',
              marginBottom: '20px',
              lineHeight: '1.5',
            }}
          >
            Enter a name for your new bot and choose a template from the list below.
          </div>

          <div className='modern-form-group'>
            <label htmlFor='input-bot-name'>
              {lang.tr('admin.workspace.bots.create.name')} <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id='input-bot-name'
              type='text'
              tabIndex={1}
              className='modern-input'
              placeholder={lang.tr('admin.workspace.bots.create.namePlaceholder')}
              minLength={0}
              maxLength={50}
              required
              value={botName}
              onChange={handleNameChanged}
              autoFocus
            />
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
              {lang.tr('admin.workspace.bots.create.nameHelper')}
            </div>
          </div>

          {templates.length > 0 && (
            <div className='modern-form-group' style={{ marginTop: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
                Select a Template <span style={{ color: '#ef4444' }}>*</span>
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginTop: '8px' }}>
                {templates.map((temp) => {
                  const isSelected = selectedTemplate?.id === temp.id

                  let iconName = 'cube'
                  let descText = 'A starter template blueprint.'
                  if (temp.id === 'insurance-bot') {
                    iconName = 'shield'
                    descText = 'Support, renew, cancel, etc.'
                  } else if (temp.id === 'salon-bot') {
                    iconName = 'flame'
                    descText = 'Style-Selection, Bookings, & cancellations'
                  } else if (temp.id === 'empty-bot') {
                    iconName = 'document'
                    descText = 'Blank canvas to build from scratch.'
                  }

                  return (
                    <div
                      key={temp.id}
                      onClick={() => handleTemplateChanged(temp)}
                      style={{
                        border: isSelected ? '2px solid #028ec0' : '1px solid #cbd5e1',
                        borderRadius: '10px',
                        padding: '14px 10px',
                        backgroundColor: isSelected ? '#f0f9ff' : '#ffffff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        position: 'relative',
                        boxShadow: isSelected ? '0 4px 12px rgba(2, 142, 192, 0.15)' : 'none',
                      }}
                      className={isSelected ? '' : 'template-card-hover'}
                    >
                      {/* Checkmark icon for selected card */}
                      {isSelected && (
                        <div style={{ position: 'absolute', top: '8px', right: '8px', color: '#028ec0' }}>
                          <Icon icon='tick-circle' iconSize={14} />
                        </div>
                      )}

                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: isSelected ? '#e0f2fe' : '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '10px',
                          color: isSelected ? '#028ec0' : '#64748b'
                        }}
                      >
                        <Icon icon={iconName as any} iconSize={20} />
                      </div>

                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a', marginBottom: '4px' }}>
                        {temp.name}
                      </div>

                      <div style={{ fontSize: '10.5px', color: '#64748b', lineHeight: '1.4' }}>
                        {descText}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button
              onClick={toggleDialog}
              className='modern-btn-secondary'
              text='Cancel'
              style={{ padding: '8px 18px' }}
            />
            <Button
              id='btn-modal-next'
              type='submit'
              text={isProcessing ? lang.tr('pleaseWait') : 'Next'}
              onClick={openChannelDialog}
              disabled={isButtonDisabled()}
              className='modern-btn-primary'
              style={{ padding: '8px 24px' }}
            />
          </div>
        </div>
      </form>
    </Dialog>
  )
}

export default BotInfoDialog
